import { useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useGetBusinessById, useGetReviews, useSubmitReview, useGetDeals, useGetEvents, getGetDealsQueryKey, getGetEventsQueryKey } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import { GoogleAd } from "@/components/GoogleAd";
import { QueryState, isNotFoundError } from "@/components/QueryState";

const CATEGORY_LABELS: Record<string, string> = {
  quiz_night: "Quiz Night", karaoke: "Karaoke", live_music: "Live Music",
  sport: "Sport", darts: "Darts", pool: "Pool", open_mic: "Open Mic",
  comedy: "Comedy", dj: "DJ Night", theme_night: "Theme Night",
  happy_hour: "Happy Hour", sports_tv: "Sports TV", ufc: "UFC/Boxing",
  pool_table: "Pool Table", dart_board: "Darts Board", jukebox: "Jukebox",
  beer_garden: "Beer Garden", other: "Event",
};
const FREQ_LABELS: Record<string, string> = {
  weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly",
  once: "One-off", permanent: "Always Available",
};
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatOpeningHours(value: string): string {
  if (!value.trim().startsWith("{")) return value;
  try {
    const hours = JSON.parse(value) as Record<string, { open?: string; close?: string } | null>;
    const days: Array<[string, string]> = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
    return days.map(([key, label]) => hours[key] ? `${label}: ${hours[key]!.open} - ${hours[key]!.close}` : `${label}: Closed`).join("\n");
  } catch { return value; }
}

function StarSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button key={s} type="button" onClick={() => onChange(s)} className="text-2xl transition-transform hover:scale-110">
          <span className={s <= value ? "text-secondary" : "text-slate-300"}>★</span>
        </button>
      ))}
    </div>
  );
}

function ReviewStars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`text-sm ${s <= Math.round(rating) ? "text-secondary" : "text-slate-300"}`}>★</span>
      ))}
    </div>
  );
}

export default function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const [reviewerName, setReviewerName] = useState(user?.name ?? "");
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, isError, error, refetch } = useGetBusinessById(id!);
  const numericId = data?.id ?? (id && /^\d+$/.test(id) ? Number(id) : 0);
  const { data: reviewsData, refetch: refetchReviews } = useGetReviews(
    { targetType: "business", targetId: numericId, approved: true }
  );
  const { mutateAsync: submitReview } = useSubmitReview();
  const { data: dealsData } = useGetDeals(
    { businessName: data?.name },
    { query: { enabled: !!data?.name, queryKey: getGetDealsQueryKey({ businessName: data?.name }) } }
  );
  const { data: eventsData } = useGetEvents(
    { businessName: data?.name },
    { query: { enabled: !!data?.name, queryKey: getGetEventsQueryKey({ businessName: data?.name }) } }
  );
  const venueId = (data as unknown as { venueId?: number | null })?.venueId ?? null;
  const venueSlug = (data as unknown as { venueSlug?: string | null })?.venueSlug ?? null;
  const { data: venueActivitiesData } = useQuery<{ items: Array<{ id: number; title: string; category: string; frequency: string; dayOfWeek: number | null; startTime: string | null; priceText: string | null; isPaid: boolean }> }>({
    queryKey: ["venue-activities", venueId],
    queryFn: () => fetch(`/api/venues/${venueId}/activities`).then((r) => r.json()) as Promise<{ items: Array<{ id: number; title: string; category: string; frequency: string; dayOfWeek: number | null; startTime: string | null; priceText: string | null; isPaid: boolean }> }>,
    enabled: !!venueId,
  });
  const bizDeals = dealsData?.items ?? [];
  const bizEvents = eventsData?.items ?? [];

  useSEO({
    title: data ? `${data.name}${data.category ? ` — ${data.category}` : ""} in ${data.town}` : undefined,
    description: data?.description ?? undefined,
    image: data?.imageUrl ?? undefined,
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": data.name,
      "description": data.description,
      "url": `https://www.ayrshirehub.co.uk/businesses/${data.slug ?? data.id}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": data.town,
        "addressRegion": "Ayrshire",
        "addressCountry": "GB",
      },
      ...(data.phone ? { "telephone": data.phone } : {}),
      ...(data.website ? { "sameAs": data.website } : {}),
      ...(data.imageUrl ? { "image": data.imageUrl } : {}),
      ...(reviewsData?.total
        ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": String(Math.round((reviewsData as unknown as { averageRating?: number }).averageRating ?? 5)), "reviewCount": String(reviewsData.total) } }
        : {}),
    } : undefined,
  });

  const handleReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewerName || !reviewText) { toast({ title: "Please fill in your name and review.", variant: "destructive" }); return; }
    if (rating < 1) { toast({ title: "Please select a star rating.", variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      await submitReview({ data: { reviewerName, targetType: "business", targetId: Number(id), rating, reviewText } });
      setReviewText(""); setRating(5);
      await refetchReviews();
      toast({ title: "Review submitted!", description: "Thanks — it will appear once approved." });
    } catch {
      toast({ title: "Error", description: "Could not submit review. Try submitting again.", variant: "destructive" });
    } finally {
      setSubmitting(false); }
  };

  // 404 = this business really doesn't exist, so keep the "not found" copy.
  // Anything else is a transport/server failure the user can retry.
  const isMissing = !isLoading && (isNotFoundError(error) || (!isError && !data));

  if (isLoading || (isError && !isMissing)) {
    return (
      <QueryState
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        skeleton={
          <div className="container mx-auto px-4 py-12 max-w-3xl">
            <div className="h-56 bg-slate-200 rounded-2xl animate-pulse mb-6" />
            <div className="space-y-3">
              <div className="h-8 bg-slate-200 rounded animate-pulse w-2/3" />
              <div className="h-4 bg-slate-200 rounded animate-pulse w-1/3" />
            </div>
          </div>
        }
        errorTitle="Couldn't load this business"
        errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
        errorAction={{ label: "Back to Businesses", href: "/businesses" }}
        fullPage
      />
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4">🏪</p>
        <p className="text-slate-500 font-medium">Business not found.</p>
        <Link href="/businesses"><Button variant="outline" className="mt-4">Back to Businesses</Button></Link>
      </div>
    );
  }

  const reviews = reviewsData?.items ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-400 flex-1 min-w-0">
            <Link href="/" className="hover:text-primary transition-colors whitespace-nowrap">Home</Link>
            <span aria-hidden="true">/</span>
            <Link href="/businesses" className="hover:text-primary transition-colors whitespace-nowrap">Businesses</Link>
            <span aria-hidden="true">/</span>
            <span className="text-slate-600 truncate" aria-current="page">{data.name}</span>
          </nav>
          {venueId && (
            <Link href={`/venues/${venueSlug ?? venueId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full hover:bg-primary/20 transition-colors">
              🍺 View Venue Page
            </Link>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {data.imageUrl ? (
              <img src={data.imageUrl} alt={data.name} className="w-full h-56 sm:h-72 object-cover" />
            ) : (
              <div className="h-40 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <span className="text-6xl">🏪</span>
              </div>
            )}
            <div className="p-6 sm:p-8 space-y-4">
              <div>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-slate-800">{data.name}</h1>
                  {data.verified && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full">
                      ✓ Verified
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className="bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">{data.category}</span>
                  {data.featured && <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">Featured</span>}
                </div>
                {data.rating > 0 && (
                  <div className="flex items-center gap-2">
                    <ReviewStars rating={data.rating} />
                    <span className="text-sm font-semibold text-slate-700">{data.rating.toFixed(1)}</span>
                    <span className="text-sm text-slate-400">({data.reviewCount} reviews)</span>
                  </div>
                )}
              </div>

              {data.description && (
                <p className="text-slate-600 leading-relaxed">{data.description}</p>
              )}

              <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-2.5">
                {data.address && <div className="flex items-start gap-2 text-sm"><span>📍</span><span className="text-slate-700">{data.address}</span></div>}
                {!data.address && <div className="flex items-center gap-2 text-sm"><span>📍</span><span className="text-slate-700">{data.town}</span></div>}
                {data.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <span>📞</span>
                    <a href={`tel:${data.phone}`} className="text-primary hover:underline font-medium">{data.phone}</a>
                  </div>
                )}
                {data.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <span>📧</span>
                    <a href={`mailto:${data.email}`} className="text-primary hover:underline">{data.email}</a>
                  </div>
                )}
                {data.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <span>🌐</span>
                    <a href={data.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{data.website.replace(/^https?:\/\//, "")}</a>
                  </div>
                )}
                {data.openingHours && (
                  <div className="flex items-start gap-2 text-sm">
                    <span>🕐</span><span className="text-slate-700 whitespace-pre-line">{formatOpeningHours(data.openingHours)}</span>
                  </div>
                )}
              </div>

              <div className="my-2">
                <GoogleAd />
              </div>

              {(data as unknown as { orderStoreSlug?: string | null }).orderStoreSlug && (
                <a
                  href={`/order/${(data as unknown as { orderStoreSlug: string }).orderStoreSlug}`}
                  className="mt-2 flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors"
                >
                  🛒 Order Online
                </a>
              )}
            </div>
          </div>

          {/* Deals from this business */}
          {bizDeals.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4">🏷️ Current Deals</h2>
              <div className="space-y-3">
                {bizDeals.map((deal) => (
                  <Link key={deal.id} href={`/deals/${deal.slug ?? deal.id}`}>
                    <div className="flex gap-3 items-center p-3 rounded-xl border border-slate-100 hover:border-secondary/40 hover:bg-amber-50 dark:bg-amber-900/20/40 transition-colors cursor-pointer">
                      {deal.imageUrl ? (
                        <img src={deal.imageUrl} alt={deal.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 text-2xl">🏷️</div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-800 truncate">{deal.title}</p>
                        <p className="text-xs text-slate-500 truncate">{deal.description}</p>
                        {deal.endDate && (
                          <p className="text-xs text-secondary font-semibold mt-0.5">
                            Until {new Date(deal.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </p>
                        )}
                      </div>
                      <svg className="flex-shrink-0 text-slate-300 ml-auto" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Events from this business */}
          {bizEvents.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4">📅 Upcoming Events</h2>
              <div className="space-y-3">
                {bizEvents.map((event) => (
                  <Link key={event.id} href={`/events/${event.slug ?? event.id}`}>
                    <div className="flex gap-3 items-center p-3 rounded-xl border border-slate-100 hover:border-primary/40 hover:bg-blue-50 dark:bg-blue-900/20/40 transition-colors cursor-pointer">
                      {event.imageUrl ? (
                        <img src={event.imageUrl} alt={event.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 text-2xl">📅</div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-800 truncate">{event.name}</p>
                        {event.eventDate && (
                          <p className="text-xs text-primary font-semibold mt-0.5">
                            {new Date(event.eventDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                            {event.eventTime ? ` · ${event.eventTime}` : ""}
                          </p>
                        )}
                        {event.venue && <p className="text-xs text-slate-500 truncate">{event.venue}</p>}
                      </div>
                      <svg className="flex-shrink-0 text-slate-300 ml-auto" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* What's On — shown when this business is registered as a venue */}
          {venueId && venueActivitiesData && venueActivitiesData.items.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-800">🍺 What's On Here</h2>
                <Link href={`/venues/${venueSlug ?? venueId}`} className="text-sm text-primary font-semibold hover:underline">
                  Full Venue Page →
                </Link>
              </div>
              <div className="space-y-2">
                {venueActivitiesData.items.map((act) => {
                  const isPermanent = act.frequency === "permanent";
                  return (
                    <div key={act.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-lg">
                        {act.category === "quiz_night" ? "🧠" : act.category === "karaoke" ? "🎤" : act.category === "live_music" ? "🎸" : act.category === "darts" || act.category === "dart_board" ? "🎯" : act.category === "pool" || act.category === "pool_table" ? "🎱" : act.category === "sports_tv" || act.category === "ufc" ? "📺" : act.category === "jukebox" ? "🎵" : act.category === "comedy" ? "😄" : act.category === "beer_garden" ? "🌿" : "🍺"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-800">{act.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {CATEGORY_LABELS[act.category] ?? act.category}
                          {!isPermanent && act.dayOfWeek !== null ? ` · ${FREQ_LABELS[act.frequency] ?? act.frequency} · ${DAY_NAMES[act.dayOfWeek]}` : ""}
                          {!isPermanent && act.startTime ? ` · ${act.startTime.slice(0, 5)}` : ""}
                          {isPermanent ? " · Always available" : ""}
                        </p>
                      </div>
                      {act.isPaid && act.priceText && (
                        <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full flex-shrink-0">{act.priceText}</span>
                      )}
                      {!act.isPaid && (
                        <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex-shrink-0">Free</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">
              Reviews {reviews.length > 0 && <span className="text-slate-400 font-normal text-sm">({reviews.length})</span>}
            </h2>
            {reviews.length === 0 ? (
              <p className="text-slate-400 text-sm py-4 text-center">No reviews yet — be the first!</p>
            ) : (
              <div className="space-y-3 mb-6">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-slate-800">{r.reviewerName}</span>
                      <ReviewStars rating={r.rating} />
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{r.reviewText}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-slate-800 mb-4">Leave a Review</h3>
              <form onSubmit={handleReview} className="space-y-3">
                <Input required placeholder="Your name" value={reviewerName} onChange={e => setReviewerName(e.target.value)} />
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Your rating</label>
                  <StarSelector value={rating} onChange={setRating} />
                </div>
                <Textarea required rows={3} placeholder="Write your review..." value={reviewText} onChange={e => setReviewText(e.target.value)} />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit Review"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
